import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { usePermissions } from '../context/PermissionContext';
import { AccessType } from '../../../types/access';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { FALLBACK_ACCESSIBLE_PATH, resolveDefaultAccessiblePath } from '../utils/getDefaultAccessiblePath';

interface PermissionRouteProps {
  children: React.ReactNode;
  requiredPermission: AccessType | string;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

function useResolvedRedirect(redirectTo?: string): string {
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { isSuperAdmin } = useAccessControl();

  const preferredRedirect = redirectTo ?? resolveDefaultAccessiblePath(hasPermission, isSuperAdmin);
  if (preferredRedirect !== location.pathname) {
    return preferredRedirect;
  }

  return FALLBACK_ACCESSIBLE_PATH !== location.pathname ? FALLBACK_ACCESSIBLE_PATH : '/';
}

export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  children,
  requiredPermission,
  fallback,
  redirectTo,
}) => {
  const { hasPermission, isLoading } = usePermissions();
  const resolvedRedirect = useResolvedRedirect(redirectTo);

  // console.log('🔐 PermissionRoute - requiredPermission:', requiredPermission);
  // console.log('🔐 PermissionRoute - isLoading:', isLoading);

  // Mostrar loading enquanto carrega permissões
  if (isLoading) {
    // console.log('🔐 PermissionRoute - showing loading state');
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="200px"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Verificando permissões...
        </Typography>
      </Box>
    );
  }

  // Verificar se tem a permissão necessária
  const hasAccess = hasPermission(requiredPermission);
  // console.log('🔐 PermissionRoute - hasAccess:', hasAccess);

  if (!hasAccess) {
    // console.log('🔐 PermissionRoute - access denied, showing fallback');
    // Se forneceu um fallback customizado, usar ele
    if (fallback) {
      return <>{fallback}</>;
    }

    return <Navigate to={resolvedRedirect} replace />;
  }

  return <>{children}</>;
};

// Componente para múltiplas permissões (OR - qualquer uma)
interface AnyPermissionRouteProps {
  children: React.ReactNode;
  requiredPermissions: (AccessType | string)[];
  fallback?: React.ReactNode;
  redirectTo?: string;
}

export const AnyPermissionRoute: React.FC<AnyPermissionRouteProps> = ({
  children,
  requiredPermissions,
  fallback,
  redirectTo,
}) => {
  const { hasAnyPermission, isLoading } = usePermissions();
  const resolvedRedirect = useResolvedRedirect(redirectTo);

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="200px"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Verificando permissões...
        </Typography>
      </Box>
    );
  }

  if (!hasAnyPermission(requiredPermissions)) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return <Navigate to={resolvedRedirect} replace />;
  }

  return <>{children}</>;
};

// Componente para múltiplas permissões (AND - todas)
interface AllPermissionRouteProps {
  children: React.ReactNode;
  requiredPermissions: (AccessType | string)[];
  fallback?: React.ReactNode;
  redirectTo?: string;
}

export const AllPermissionRoute: React.FC<AllPermissionRouteProps> = ({
  children,
  requiredPermissions,
  fallback,
  redirectTo,
}) => {
  const { hasAllPermissions, isLoading } = usePermissions();
  const resolvedRedirect = useResolvedRedirect(redirectTo);

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="200px"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Verificando permissões...
        </Typography>
      </Box>
    );
  }

  if (!hasAllPermissions(requiredPermissions)) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return <Navigate to={resolvedRedirect} replace />;
  }

  return <>{children}</>;
};
